import { styled } from '@mui/material'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import React from 'react'
import { useSelector } from 'react-redux'

import Tooltip from '@/app/component/atom/tooltip'
import { Form } from '@/app/component/form'
import SubmitButton from '@/app/component/form/submit-button'
import Loading from '@/app/component/guard/loading'
import { useCreateContact } from '@/app/module/contacts/hooks/use-create-contact'
import { useGetContactPermissions } from '@/app/module/contacts/hooks/use-get-contact-permissions'
import { CreateContactValues } from '@/app/module/contacts/types'
import { useGetCustomFields } from '@/app/module/custom-fields/hooks/use-get-custom-fields'
import { selectDefaultCountryCode, selectLanguages } from '@/app/module/utils/store/selectors'
import { SelectOption } from '@/app/types'

import { ActionsContainer, FormContainer } from './common'
import { getCreateContactFields, getCreateContactSchema } from './specs'
import { filterRestrictedFieldValues } from './utils'

type Props = {
  activeFilter?: { name: string; value: string }

  onExit: (id?: number) => void
}

const CreateContactForm: React.FC<Props> = ({ activeFilter, onExit }) => {
  const [addToGroupAfterSaving, setAddToGroupAfterSaving] = React.useState(activeFilter?.name === 'groups')
  const countryCode = useSelector(selectDefaultCountryCode)
  const languageOptions = useSelector(selectLanguages) as SelectOption<string>[]
  const { data: cfData, isFetching, isLoading, isUninitialized } = useGetCustomFields()
  const [createContact, { isLoading: isCreating }] = useCreateContact()

  const customFields = React.useMemo(() => cfData?.allCustomfields || [], [cfData])

  const permissions = useGetContactPermissions()

  const isSaveDisabled = !permissions.fullPhoneNumber

  const fields = React.useMemo(
    () => getCreateContactFields(permissions, customFields, languageOptions, countryCode, isSaveDisabled),
    [countryCode, customFields, isSaveDisabled, languageOptions, permissions],
  )
  const schema = React.useMemo(() => getCreateContactSchema(customFields), [customFields])

  const defaultValues = React.useMemo(
    () => ({
      firstName: '',
      lastName: '',
      fullPhoneNumber: '',
      language: '',
      ...customFields.reduce(
        (acc, { id }) => ({
          ...acc,
          [`customFields${id}`]: '',
        }),
        {},
      ),
    }),
    [customFields],
  )

  const handleSubmit = React.useCallback(
    async (data: CreateContactValues) => {
      const created = await createContact(filterRestrictedFieldValues(permissions, customFields, data))
      if (!created) {
        return
      }

      if (addToGroupAfterSaving) {
        onExit(created.id)
      } else {
        onExit()
      }
    },
    [addToGroupAfterSaving, customFields, permissions, createContact, onExit],
  )

  const showLoading = isCreating || isFetching || isLoading || isUninitialized
  const showForm = !isFetching && !isLoading && !isUninitialized

  return (
    <Loading isLoading={showLoading} testId="contact-item">
      {!permissions.fullPhoneNumber && (
        <AlertBanner severity="warning">
          You cannot create a contact. You don't have permission to add or edit contact phone numbers, which is required
          to create a contact.
        </AlertBanner>
      )}
      <AddToGroupContainer>
        <Tooltip title="You'll be able to select the group(s) after saving the contact.">
          <FormControlLabel
            control={
              <Checkbox
                id="add-edit-contact-add-to-group-checkbox"
                disabled={isSaveDisabled}
                checked={addToGroupAfterSaving}
                onChange={(e) => setAddToGroupAfterSaving(e.currentTarget.checked)}
              />
            }
            label="Also add this contact to a group(s)"
          />
        </Tooltip>
      </AddToGroupContainer>
      {showForm && (
        <FormContainer id="contact-item-form">
          <Form
            defaultValues={defaultValues}
            fields={fields}
            schema={schema}
            shouldReset
            onSubmit={handleSubmit}
            renderActions={({ isDirty }) => (
              <ActionsContainer>
                <Button id="contact-back-button" variant="outlined" onClick={() => onExit()}>
                  Back
                </Button>
                <SubmitButton id="contact-save-button" isDirty={isDirty} isSubmitting={isCreating}>
                  Save
                </SubmitButton>
              </ActionsContainer>
            )}
          />
        </FormContainer>
      )}
    </Loading>
  )
}

const AddToGroupContainer = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
  width: '100%',
  marginTop: -30,

  '& .MuiFormControlLabel-root': {
    marginRight: 0,
  },
})

const AlertBanner = styled(Alert)({
  marginBottom: 32,
})

export default CreateContactForm
